import { useEffect, useMemo, useState } from "react";
import {
  api,
  fmtDate,
  fmtMoney,
  TARIFF_LABEL,
  TARIFF_ORDER,
  type ActivationCode,
  type PlanInfo,
  type Tariff,
} from "../api";
import { CodeUsedBadge, TariffBadge } from "../components/Badge";
import { Modal } from "../components/Modal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { downloadCsv } from "../util/csv";

export function Codes({
  openCreate,
  setOpenCreate,
}: {
  openCreate: boolean;
  setOpenCreate: (v: boolean) => void;
}) {
  const [rows, setRows] = useState<ActivationCode[] | null>(null);
  const [q, setQ] = useState("");
  const [filterUsed, setFilterUsed] = useState<"" | "true" | "false">("");
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [toDelete, setToDelete] = useState<ActivationCode | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setRows(null);
        const opts: { used?: boolean } = {};
        if (filterUsed === "true") opts.used = true;
        if (filterUsed === "false") opts.used = false;
        const r = await api.activationCodes(opts);
        setRows(r);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [refresh, filterUsed]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toUpperCase();
    if (!ql) return rows;
    return rows.filter((c) => c.code.includes(ql));
  }, [rows, q]);

  function exportCsv() {
    const header = ["Kod", "Tarif", "Foydalanilgan", "Ustaxona", "Yaratilgan"];
    const data = filtered.map((c) => [
      c.code,
      TARIFF_LABEL[c.tariff],
      c.used ? "Ha" : "Yo'q",
      c.workshop_name ?? "",
      c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : "",
    ]);
    downloadCsv(`kodlar-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...data]);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Aktivatsiya kodlari</h1>
          <p className="page-sub">Ustaxonalarning tarifini faollashtirish uchun bir martalik kodlar</p>
        </div>
        <div className="actions">
          <button className="btn ghost" onClick={exportCsv} disabled={!rows || rows.length === 0}>CSV eksport</button>
          <button className="btn" onClick={() => setOpenCreate(true)}>+ Yangi kod</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="filters">
        <label className="grow">
          Qidiruv (kod)
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Masalan ABC123" />
        </label>
        <label>
          Holat
          <select value={filterUsed} onChange={(e) => setFilterUsed(e.target.value as "" | "true" | "false")}>
            <option value="">Hammasi</option>
            <option value="false">Yangi</option>
            <option value="true">Foydalanilgan</option>
          </select>
        </label>
      </div>

      <div className="panel flush">
        {!rows ? <div className="empty">Yuklanmoqda…</div> :
          filtered.length === 0 ? <div className="empty">Mos kod yo'q</div> : (
            <table className="table">
              <thead><tr>
                <th>Kod</th><th>Tarif</th><th>Holat</th><th>Bog'langan ustaxona</th><th>Yaratilgan</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="mono"><strong>{c.code}</strong></td>
                    <td><TariffBadge tariff={c.tariff} /></td>
                    <td><CodeUsedBadge used={c.used} /></td>
                    <td>{c.workshop_name ?? <span className="muted">—</span>}</td>
                    <td className="muted">{fmtDate(c.created_at)}</td>
                    <td className="right">
                      {!c.used && (
                        <button
                          className="icon-btn danger"
                          onClick={() => setToDelete(c)}
                        >
                          O'chirish
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {openCreate && (
        <CreateModal
          onClose={() => setOpenCreate(false)}
          onSaved={() => { setOpenCreate(false); setRefresh((x) => x + 1); }}
        />
      )}

      {toDelete && (
        <ConfirmDialog
          title="Kodni o'chirish"
          body={<><span className="mono"><strong>{toDelete.code}</strong></span> kodi o'chirilsinmi? Bu amalni qaytarib bo'lmaydi.</>}
          confirmLabel="Ha, o'chirish"
          danger
          onCancel={() => setToDelete(null)}
          onConfirm={async () => {
            await api.deleteActivationCode(toDelete.id);
            setToDelete(null);
            setRefresh((x) => x + 1);
          }}
        />
      )}
    </>
  );
}

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [tariff, setTariff] = useState<Tariff>("standard");
  const [count, setCount] = useState("1");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<string[] | null>(null);
  const [plans, setPlans] = useState<PlanInfo[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setPlans(await api.plans());
      } catch { /* narx ko'rsatish ixtiyoriy — xatoda dropdown narxsiz ishlaydi */ }
    })();
  }, []);

  // Narx yagona manbadan (plan_limits) keladi; trial → "Bepul".
  function optionLabel(t: Tariff): string {
    const price = plans.find((p) => p.tariff === t)?.price;
    const suffix =
      price == null ? "Bepul" : `${fmtMoney(price)}/oy`;
    return `${TARIFF_LABEL[t]} — ${suffix}`;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const n = Math.max(1, Math.min(100, parseInt(count, 10) || 1));
      if (n === 1) {
        const r = await api.createActivationCode(tariff);
        setCreated([r.code]);
      } else {
        const r = await api.createActivationCodesBulk(tariff, n);
        setCreated(r.map((c) => c.code));
      }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  function copyAll() {
    if (!created) return;
    navigator.clipboard?.writeText(created.join("\n"));
  }

  if (created) {
    return (
      <Modal onClose={onClose} labelledBy="codes-done-title">
        <div className="modal-head">
          <h2 id="codes-done-title">{created.length} ta kod yaratildi</h2>
          <div className="sub">Kodlarni ustaxona egalariga yuboring</div>
        </div>
        <div className="modal-body">
          <pre style={{
            fontFamily: "var(--font-data)",
            background: "var(--panel-2)",
            padding: 12,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--line)",
            fontSize: "var(--text-md)",
            maxHeight: 300,
            overflow: "auto",
            margin: 0,
          }}>{created.join("\n")}</pre>
        </div>
        <div className="actions between">
          <button className="btn ghost" onClick={copyAll}>Hammasini nusxalash</button>
          <button className="btn" onClick={() => { onSaved(); onClose(); }}>Yopish</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} labelledBy="codes-new-title">
      <form onSubmit={submit}>
        <div className="modal-head"><h2 id="codes-new-title">Yangi aktivatsiya kodi</h2></div>
        <div className="modal-body">
          {err && <div className="error">{err}</div>}
          <div className="form">
            <div className="grid-2">
              <div className="field">
                <label>Tarif</label>
                <select value={tariff} onChange={(e) => setTariff(e.target.value as Tariff)}>
                  {TARIFF_ORDER.map((t) => (
                    <option key={t} value={t}>{optionLabel(t)}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Soni</label>
                <input type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} />
                <div className="help">Bir vaqtning o'zida 100 tagacha kod yaratish mumkin.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>Bekor</button>
          <button type="submit" className="btn" disabled={busy}>{busy ? "..." : "Yaratish"}</button>
        </div>
      </form>
    </Modal>
  );
}
